// src/pages/RegisterPartner.jsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getAuth,
  createUserWithEmailAndPassword,
  updateProfile,
} from "firebase/auth";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../contexts/firebase/config";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  InputAdornment,
  IconButton,
  Snackbar,
  Stack,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Fade,
} from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import BadgeIcon from "@mui/icons-material/Badge";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneIcon from "@mui/icons-material/Phone";

export default function RegisterPartner() {
  const navigate = useNavigate();
  const auth = getAuth();

  const [fields, setFields] = useState({
    nome: "",
    email: "",
    senha: "",
    nif: "",
    morada: "",
    contacto: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [terms, setTerms] = useState(false);
  const [error, setError] = useState("");
  const [openSnack, setOpenSnack] = useState(false);
  const [loading, setLoading] = useState(false);

  const toggleShowPassword = () => setShowPassword((v) => !v);

  const isValidEmail = (email) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email).toLowerCase());

  const validate = () => {
    if (!fields.nome.trim()) return "Insere o nome da empresa.";
    if (!isValidEmail(fields.email)) return "E-mail inválido.";
    if (fields.senha.length < 6)
      return "A palavra-passe deve ter pelo menos 6 caracteres.";
    if (!terms)
      return "Tens de aceitar os Termos & Condições para continuar.";
    return null;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFields((f) => ({ ...f, [name]: value }));
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");
    const vErr = validate();
    if (vErr) {
      setError(vErr);
      setOpenSnack(true);
      return;
    }

    setLoading(true);
    try {
      // Cria conta
      const { user } = await createUserWithEmailAndPassword(
        auth,
        fields.email.trim(),
        fields.senha
      );

      // Atualiza nome de exibição
      await updateProfile(user, { displayName: fields.nome.trim() });

      // Grava empresa no Firestore
      await setDoc(doc(db, "empresas", user.uid), {
        nomeEmpresa: fields.nome.trim(),
        email: fields.email.trim().toLowerCase(),
        nif: fields.nif || null,
        morada: fields.morada || null,
        contacto: fields.contacto || null,
        createdAt: serverTimestamp(),
        role: "partner",
        aceitouTermos: terms,
      });

      navigate("/login", { replace: true });
    } catch (err) {
      console.error(err);
      setError(err?.message || "Erro ao criar conta. Tenta novamente.");
      setOpenSnack(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.default",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 3,
      }}
    >
      <Paper
        elevation={6}
        sx={{
          width: "100%",
          maxWidth: 600,
          borderRadius: 3,
          p: { xs: 3, md: 5 },
        }}
      >
        <Stack spacing={3}>
          <Box textAlign="center">
            <Typography variant="h4" fontWeight={800} gutterBottom>
              Registar Parceiro
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Cria uma conta para gerir os teus clientes e comissões.
            </Typography>
          </Box>

          {error && (
            <Fade in={!!error}>
              <Alert severity="error" onClose={() => setError("")}>
                {error}
              </Alert>
            </Fade>
          )}

          <Box component="form" onSubmit={handleRegister} noValidate>
            <Stack spacing={2}>
              <TextField
                label="Nome da Empresa"
                name="nome"
                fullWidth
                required
                value={fields.nome}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <BusinessIcon />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label="E-mail"
                name="email"
                type="email"
                fullWidth
                required
                value={fields.email}
                onChange={handleChange}
                error={fields.email.length > 0 && !isValidEmail(fields.email)}
                helperText={
                  fields.email.length > 0 && !isValidEmail(fields.email)
                    ? "E-mail inválido"
                    : ""
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label="Palavra-passe"
                name="senha"
                type={showPassword ? "text" : "password"}
                fullWidth
                required
                value={fields.senha}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={toggleShowPassword} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                helperText="Mínimo 6 caracteres"
              />

              <TextField
                label="NIF"
                name="nif"
                value={fields.nif}
                onChange={handleChange}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <BadgeIcon />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label="Morada"
                name="morada"
                value={fields.morada}
                onChange={handleChange}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationOnIcon />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label="Contacto"
                name="contacto"
                value={fields.contacto}
                onChange={handleChange}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon />
                    </InputAdornment>
                  ),
                }}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={terms}
                    onChange={(e) => setTerms(e.target.checked)}
                  />
                }
                label={
                  <Typography variant="body2">
                    Aceito os{" "}
                    <a
                      href="/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "inherit" }}
                    >
                      Termos & Condições
                    </a>{" "}
                    da plataforma
                  </Typography>
                }
              />

              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                disabled={loading}
                sx={{
                  py: 1.4,
                  fontWeight: 700,
                  textTransform: "none",
                  borderRadius: 2,
                }}
              >
                {loading ? (
                  <CircularProgress size={26} color="inherit" />
                ) : (
                  "Criar Conta de Parceiro"
                )}
              </Button>
            </Stack>
          </Box>

          <Typography variant="body2" align="center">
            Já tens uma conta?{" "}
            <a
              href="/login"
              style={{
                color: "#1976d2",
                fontWeight: 600,
                textDecoration: "none",
              }}
            >
              Iniciar sessão
            </a>
          </Typography>
        </Stack>

        <Snackbar
          open={openSnack}
          autoHideDuration={6000}
          onClose={() => setOpenSnack(false)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          {error ? <Alert severity="error">{error}</Alert> : null}
        </Snackbar>
      </Paper>
    </Box>
  );
}
